import { useCallback, useMemo } from "react";

import { useStore } from "../../../state/store";
import type { SamplesPanelGridState } from "../../types";
import { getFieldKey, type PickerColumn } from "../gridUtils";

interface UseSampleGridStateResult extends SamplesPanelGridState {
  columnVisibility: Record<string, boolean>;
  setColumnVisibility: (visibility: Record<string, boolean>) => void;
  /** Merge a partial sorting/filters/sizing update into the persisted
   *  scope entry (see `patchSamplesGridState`). */
  patchGridState: (partial: Partial<SamplesPanelGridState>) => void;
}

/**
 * Persistence for the cross-log SamplesPanel grid. Owns its own
 * column-visibility map plus the TanStack grid state (sorting, per-column
 * filters, column widths), all store-backed so they survive the panel
 * unmounting on sample navigation. The returned visibility map merges
 * `defaultsForUnseededColumns` under the persisted entries — defaults are
 * derived, not written to the store, so only user toggles persist and a
 * never-toggled column's default keeps tracking the data.
 *
 * The per-task SampleList scope (`logViewSamples`) flows through
 * `useSamplesView` instead — see `apps/inspect/src/app/samples/list/`.
 */
export function useSampleGridState(
  scope: "samplesPanel",
  allColumns: PickerColumn[],
  options?: {
    defaultsForUnseededColumns?: (col: PickerColumn) => boolean;
  }
): UseSampleGridStateResult {
  const { defaultsForUnseededColumns } = options ?? {};
  const persistedVisibility = useStore(
    (state) => state.logs.samplesListState.byScope[scope].columnVisibility
  );
  const sorting = useStore(
    (state) => state.logs.samplesListState.byScope[scope].sorting
  );
  const columnFilters = useStore(
    (state) => state.logs.samplesListState.byScope[scope].columnFilters
  );
  const columnSizing = useStore(
    (state) => state.logs.samplesListState.byScope[scope].columnSizing
  );
  const setSamplesColumnVisibility = useStore(
    (state) => state.logsActions.setSamplesColumnVisibility
  );
  const patchSamplesGridState = useStore(
    (state) => state.logsActions.patchSamplesGridState
  );

  const columnVisibility = useMemo(() => {
    const defaults = defaultsForUnseededColumns
      ? Object.fromEntries(
          allColumns.map((col) => [
            getFieldKey(col),
            defaultsForUnseededColumns(col),
          ])
        )
      : {};
    return { ...defaults, ...persistedVisibility };
  }, [allColumns, defaultsForUnseededColumns, persistedVisibility]);

  const setColumnVisibility = useCallback(
    (visibility: Record<string, boolean>) => {
      // ColumnSelectorPopover emits the full effective map, but persisting it
      // wholesale would freeze the derived defaults of every untouched column
      // (e.g. error/limit auto-promote when data appears). Persist only the
      // entries that differ from the current effective visibility.
      const changed = Object.fromEntries(
        Object.entries(visibility).filter(
          ([key, visible]) => columnVisibility[key] !== visible
        )
      );
      if (Object.keys(changed).length > 0) {
        setSamplesColumnVisibility(scope, {
          ...persistedVisibility,
          ...changed,
        });
      }
    },
    [scope, setSamplesColumnVisibility, columnVisibility, persistedVisibility]
  );

  const patchGridState = useCallback(
    (partial: Partial<SamplesPanelGridState>) => {
      patchSamplesGridState(scope, partial);
    },
    [scope, patchSamplesGridState]
  );

  return useMemo(
    () => ({
      columnVisibility,
      setColumnVisibility,
      sorting,
      columnFilters,
      columnSizing,
      patchGridState,
    }),
    [
      columnVisibility,
      setColumnVisibility,
      sorting,
      columnFilters,
      columnSizing,
      patchGridState,
    ]
  );
}
